import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Slider,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { CurrencyTextField } from "../general/CurrencyTextField";
import { DialogShell } from "../general/DialogShell";
import { TwoLiner } from "../general/TwoLiner";
import { percentage } from "../../shared/percentage";
import { PAYMENT_ITEM_TYPE, PaymentItem } from "./paymentItem.ts";
import { useTheme } from "../../context/ThemeContext";

export const ThirdPartyFundingAgreement = ({
  onPaymentItemsChange,
}: {
  onPaymentItemsChange?: (items: PaymentItem[]) => void;
}) => {
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "agreement.thirdPartyFunding",
  });
  const [successCondition, setSuccessCondition] = useState(0);
  const [investment, setInvestment] = useState(0);
  const [awardedDamages, setAwardedDamages] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [caseDetailsInfoOpen, setCaseDetailsInfoOpen] = useState(false);

  const [multiplier, setMultiplier] = useState<number | string>("");
  const [feePercentage, setFeePercentage] = useState(0);

  const [selectedOptions, setSelectedOptions] = useState<
    ("option1" | "option2")[]
  >(["option1"]);

  const handleCheckboxChange = (option: "option1" | "option2") => {
    setSelectedOptions((prev) => {
      if (prev.includes(option)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((o) => o !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  const percentageDamage = awardedDamages / 100;
  const reimbursement = investment;

  // Option 1 Calculation
  const numericMultiplier = multiplier === "" ? 0 : Number(multiplier);
  const profitOption1 = numericMultiplier * investment;

  // Option 2 Calculation
  const profitOption2 = feePercentage * percentageDamage;

  // Funder profit sums Option 1 and, if selected, Option 2.
  const funderProfit =
    profitOption1 + (selectedOptions.includes("option2") ? profitOption2 : 0);

  const totalDue = reimbursement + funderProfit;
  const amountIfLose = 0;
  const amountIfWin = totalDue;

  // Payment items for summary overview.
  const paymentItems = useMemo(() => {
    return [
      {
        id: "tpf-investment",
        name: t("paymentItems.investmentPayment"),
        secondaryText: currency.format(reimbursement),
        paymentType: PAYMENT_ITEM_TYPE.SUCCESS,
      },
      {
        id: "tpf-profit",
        name: t("paymentItems.profit"),
        secondaryText: currency.format(funderProfit),
        paymentType: PAYMENT_ITEM_TYPE.SUCCESS,
      },
    ];
  }, [reimbursement, funderProfit, t, currency]);

  useEffect(() => {
    if (onPaymentItemsChange) {
      onPaymentItemsChange(paymentItems);
    }
  }, [paymentItems, onPaymentItemsChange]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
      }}
    >
      {/* CASE DETAILS */}
      <Box
        sx={{
          alignItems: "center",
        }}
      >
        <Typography
          variant="button"
          sx={{
            flexGrow: 1,
          }}
        >
          {t("caseDetails")}
        </Typography>
        <IconButton size="small" onClick={() => setCaseDetailsInfoOpen(true)}>
          <HelpIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: 2,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{ mb: theme.spacing(2), width: "100%" }}
        >
          {/* Success Condition */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" gutterBottom>
              {t("successCondition")}
            </Typography>
            <CurrencyTextField
              value={successCondition}
              onValueChange={(val: number) => setSuccessCondition(val)}
              currency={currency}
            />
          </Grid>
          {/* Funding investment */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" gutterBottom>
              {t("fundingInvestment")}
            </Typography>
            <CurrencyTextField
              value={investment}
              onValueChange={(val: number) => setInvestment(val)}
              currency={currency}
            />
          </Grid>
          {/* Awarded damages */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" gutterBottom>
              {t("awardedDamages")}
            </Typography>
            <CurrencyTextField
              value={awardedDamages}
              onValueChange={(val: number) => setAwardedDamages(val)}
              currency={currency}
            />
          </Grid>
        </Grid>
      </Box>

      {/* FUNDER PROFIT CALCULATION PARAMETERS */}
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            mb: theme.spacing(2),
          }}
        >
          <Typography variant="button" sx={{ mr: theme.spacing(1) }}>
            {t("funderProfit")}
          </Typography>
          <IconButton size="small" edge="end" onClick={() => setInfoOpen(true)}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: theme.palette.divider,
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  mb: theme.spacing(1),
                  flexWrap: "wrap",
                }}
              >
                <Checkbox
                  checked={selectedOptions.includes("option1")}
                  onChange={() => handleCheckboxChange("option1")}
                />
                <Typography variant="body2" color="textSecondary">
                  {t("optionOneDescription")}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                }}
              >
                <TextField
                  label={t("investmentMultiplier")}
                  type="number"
                  value={multiplier}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setMultiplier(newValue === "" ? "" : Number(newValue));
                  }}
                  variant="outlined"
                  size="small"
                  disabled={!selectedOptions.includes("option1")}
                />
                <TwoLiner
                  primary={currency.format(profitOption1)}
                  secondary={t("resultingProfit")}
                  disabled={!selectedOptions.includes("option1")}
                />
              </Stack>
            </Box>
          </Grid>

          {/* Option 2*/}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: theme.palette.divider,
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  mb: theme.spacing(1),
                  flexWrap: "wrap",
                }}
              >
                <Checkbox
                  checked={selectedOptions.includes("option2")}
                  onChange={() => handleCheckboxChange("option2")}
                />
                <Typography variant="body2" color="textSecondary">
                  {t("optionTwoDescription")}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                }}
              >
                <TwoLiner
                  primary={percentage.format(feePercentage / 100)}
                  secondary={t("damagePercentage")}
                  disabled={!selectedOptions.includes("option2")}
                />
                <Slider
                  sx={{
                    mt: theme.spacing(2),
                    ml: theme.spacing(2),
                    mb: theme.spacing(2),
                    minWidth: 100,
                    maxWidth: 150,
                  }}
                  color="primary"
                  size="small"
                  value={feePercentage}
                  aria-label="Contingency fee percentage"
                  valueLabelFormat={(value) => percentage.format(value / 100)}
                  valueLabelDisplay="auto"
                  onChange={(e, value) => {
                    if (typeof value === "number") {
                      setFeePercentage(value);
                    }
                  }}
                  min={0}
                  max={100}
                  disabled={!selectedOptions.includes("option2")}
                />
                <TwoLiner
                  primary={currency.format(profitOption2)}
                  secondary={t("resultingProfit")}
                  disabled={!selectedOptions.includes("option2")}
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Calculation Returns */}
        <Box sx={{ mt: theme.spacing(2) }}>
          <Typography variant="body2">
            {t("calculateFunding.reimbursement")}
            {currency.format(reimbursement)}
          </Typography>
          <Typography variant="body2">
            {t("calculateFunding.maxProfit")} {currency.format(funderProfit)}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography
          variant="button"
          sx={{
            mt: theme.spacing(1),
          }}
        >
          {t("payments")}
        </Typography>
        <Stack
          spacing={1}
          sx={{
            mt: theme.spacing(1),
          }}
        >
          <Typography variant="body2">
            {t("totalDueOnSuccess")} {currency.format(amountIfWin)}
          </Typography>
          <Typography variant="body2">
            {t("totalDueOnFailure")} {currency.format(amountIfLose)}
          </Typography>
        </Stack>
      </Box>

      {/* Info Dialog */}
      <DialogShell
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("dialog.title")}
        description={t("dialog.dialogContent")}
        actions={
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            autoFocus
          >
            {t("dialog.dialogOk")}
          </Button>
        }
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("dialog.successFailure")}
        </Typography>
      </DialogShell>

      {/* Info Dialog for Case Details */}
      <DialogShell
        open={caseDetailsInfoOpen}
        onClose={() => setCaseDetailsInfoOpen(false)}
        title={t("caseDetailsDialog.title")}
        actions={
          <Button
            onClick={() => setCaseDetailsInfoOpen(false)}
            variant="contained"
            autoFocus
          >
            {t("dialog.dialogOk")}
          </Button>
        }
      >
        {(
          ["successCondition", "fundingInvestment", "awardedDamages"] as const
        ).map((term) => (
          <div key={term}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(term)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t(`caseDetailsDialog.descriptions.${term}`)}
            </Typography>
          </div>
        ))}
      </DialogShell>
    </Box>
  );
};
